import { mix, rgb, rgba } from 'polished';

export type Color = string;

const WHITE: Color = rgb(255, 255, 255);
const BLACK: Color = rgb(0, 0, 0);
const TRANSPARENT: Color = rgba(255, 255, 255, 0);
const OFFWHITE = rgb(244, 246, 249);
const OFFBLACK = rgb(68, 76, 95);
const BRAND = rgb(42, 87, 141);

export const template = {
  blue: {
    dark: '#4c76c4',
    light: '#aec9f9',
    normal: '#5f94f5'
  },
  pink: {
    dark: '#f06292',
    light: '#f8bbd0',
    normal: '#f48fb1'
  },
  green: {
    dark: '#23b47a',
    light: '#93e4c3',
    normal: '#28c988'
  },
  red: {
    dark: '#e04444',
    light: '#fca5a5',
    normal: '#f94c4c'
  },
  white: {
    dark: '#dfdfdf',
    normal: '#ffffff'
  },
  yellow: {
    dark: '#A38D00',
    light: '#F4E0A4',
    normal: '#F7D036'
  }
};

interface IBasePalette {
  base: Color;
}

export type LightShade = 'L95' | 'L9' | 'L8' | 'L7' | 'L6' | 'L5' | 'L4' | 'L3' | 'L2' | 'L1';
type LightShadeSet = { [shade in LightShade]: Color };
export type DarkShade = 'D1' | 'D2' | 'D3' | 'D4' | 'D5' | 'D6' | 'D7' | 'D8' | 'D9' | 'D95';
type DarkShadeSet = { [shade in DarkShade]: Color };
type PaletteShadeSet = LightShadeSet & DarkShadeSet;

export type ColorPalette = IBasePalette & PaletteShadeSet;

type CorePaletteVariant = 'base' | 1 | 2 | 3 | 4;

export type CorePalette = { [variant in CorePaletteVariant]: Color };

interface ISemanticColors {
  lightBackground: Color;
  darkBackground: Color;
  alternateBackground: Color;
  alternateChartBackground: Color;
  offBackground: Color;
  textColor: Color;
  backgroundHoverColor: Color;
}

export interface ICorePaletteMap {
  primary: CorePalette;
  secondary: CorePalette;
  core: ISemanticColors;
}

type CreatePaletteParams = {
  whitePoint?: Color;
  blackPoint?: Color;
} & Partial<PaletteShadeSet>;

function createPalette(
  base: Color,
  { whitePoint = WHITE, blackPoint = BLACK, ...shadeOverrides }: CreatePaletteParams = {}
): ColorPalette {
  return {
    D1: mix(1 / 10, blackPoint, base),
    D2: mix(2 / 10, blackPoint, base),
    D3: mix(3 / 10, blackPoint, base),
    D4: mix(4 / 10, blackPoint, base),
    D5: mix(5 / 10, blackPoint, base),
    D6: mix(6 / 10, blackPoint, base),
    D7: mix(7 / 10, blackPoint, base),
    D8: mix(8 / 10, blackPoint, base),
    D9: mix(9 / 10, blackPoint, base),
    D95: mix(0.95, blackPoint, base),
    L1: mix(1 / 10, whitePoint, base),
    L2: mix(2 / 10, whitePoint, base),
    L3: mix(3 / 10, whitePoint, base),
    L4: mix(4 / 10, whitePoint, base),
    L5: mix(5 / 10, whitePoint, base),
    L6: mix(6 / 10, whitePoint, base),
    L7: mix(7 / 10, whitePoint, base),
    L8: mix(8 / 10, whitePoint, base),
    L9: mix(9 / 10, whitePoint, base),
    L95: mix(0.95, whitePoint, base),
    base,
    ...shadeOverrides
  };
}

const offblack = createPalette(OFFBLACK, {
  D3: rgb(46, 53, 67),
  D4: '#121212',
  L3: mix(0.25, WHITE, rgb(85, 93, 112))
});
const blue = createPalette(rgb(81, 147, 253), { L95: OFFWHITE });
const pink = createPalette(rgb(244, 143, 177));
const red = createPalette(rgb(255, 53, 66));
const yellow = createPalette(rgb(255, 184, 40), { L1: rgb(241, 193, 109) });
const green = createPalette(rgb(0, 205, 130));
const brand = createPalette(BRAND);

export const light: ICorePaletteMap = {
  core: {
    alternateBackground: '#151f28', // blue.L8
    alternateChartBackground: '#1c2a35', // blue.L8
    backgroundHoverColor: 'blue', // WHITE
    darkBackground: '#131416', // blue.L95
    lightBackground: BLACK, // WHITE
    offBackground: BLACK, // blue.L9
    textColor: WHITE // '#333333'
  },
  primary: {
    base: WHITE,
    1: blue.L95,
    2: blue.L9,
    3: blue.L8,
    4: blue.L7
  },
  secondary: {
    base: offblack.base,
    1: offblack.L1,
    2: offblack.L3,
    3: offblack.D4,
    4: offblack.L5
  }
};

export const dark: ICorePaletteMap = {
  core: {
    alternateBackground: '#242424', // '#151f28', // offblack.D1,
    alternateChartBackground: BLACK, // '#1c2a35',
    backgroundHoverColor: 'blue', // offblack.D1,
    darkBackground: '#131416', // offblack.D4,
    lightBackground: BLACK, // offblack.D3,
    offBackground: '#121212', // BLACK, // offblack.base,
    textColor: WHITE
  },
  primary: {
    base: offblack.D3,
    1: offblack.D4,
    2: offblack.base,
    3: offblack.D1,
    4: offblack.D7
  },
  secondary: {
    base: WHITE,
    1: blue.L95,
    2: blue.L9,
    3: blue.L8,
    4: blue.L5
  }
};

interface IAccentPalette extends IBasePalette {
  darker: Color;
  lighter: Color;
}

export type AccentName = 'dominant' | 'good' | 'aware' | 'bad' | 'soft';

export type AccentPaletteMap = { [accent in AccentName]: IAccentPalette };

const accents: AccentPaletteMap = {
  aware: {
    base: yellow.base,
    darker: yellow.D1,
    lighter: yellow.L5
  },
  bad: {
    base: red.base,
    darker: red.D1,
    lighter: red.L5
  },
  dominant: {
    base: blue.base,
    darker: blue.D2,
    lighter: blue.L5
  },
  soft: {
    base: pink.base,
    darker: pink.D2,
    lighter: pink.L5
  },
  good: {
    base: green.base,
    darker: green.D1,
    lighter: green.L5
  }
};

const spectrum = {
  blue,
  brand,
  green,
  offblack,
  red,
  yellow
};

export const colors = {
  accents,
  dark,
  light,
  spectrum,
  static: { white: WHITE, black: BLACK, transparent: TRANSPARENT }
};
